import { CustomEpic, PayloadAction } from 'types/types';
import {
  BookAppointmentActionPayload,
  bookAppointmentFailure,
  bookAppointmentSuccess,
} from 'modules/appointments/actions';
import { ofType } from 'redux-observable';
import { BOOK_APPOINTMENT } from 'modules/appointments/actionTypes';
import { map, switchMap } from 'rxjs/operators';
import { APPOINTMENTS_ENDPOINT } from 'modules/appointments/constants';
import { getId } from 'selectors/user/getId';
import { getAuthHeaders } from 'api/getAuthHeaders';
import { handleAjaxSuccess } from 'utils/epics/handleAjaxSuccess';
import { handleAjaxError } from 'utils/epics/handleAjaxError';
import { bookAppointmentCallTranslator } from 'modules/appointments/translator';
import { Observable, of } from 'rxjs';
import { fetchPatientCases } from 'modules/cases/actions';
import { fetchPatientCases$ } from 'modules/cases/epics/fetchPatientCases$';
import { getCaseForPatient } from 'selectors/cases/getCaseForPatient';
import { FETCH_CASES_SUCCESS } from 'modules/cases/actionTypes';
import toast from 'react-hot-toast';
import { getCurrentConnectionStatus } from 'utils/connectionStatus';
import { handleOfflineBooking } from 'modules/offlineSync/utils/handleOfflineBooking';
import { getIdempotencyHeaders } from 'utils/api/getIdempotencyHeaders';
import { randomUUID } from 'utils/crypto/randomUUID';

export const bookAppointment$: CustomEpic<BookAppointmentActionPayload> = (
  action$,
  state$,
  { postApi$, getApi$ },
) =>
  action$.pipe(
    ofType(BOOK_APPOINTMENT),
    switchMap((action: PayloadAction<BookAppointmentActionPayload>) =>
      of(action),
    ),
    // Fetch cases for patient if none can be found
    switchMap(action => {
      const { patientId, caseId } = action.payload;
      if (caseId) {
        return of(action);
      }
      const patientCase = getCaseForPatient(state$.value, { patientId });
      if (!patientCase) {
        return fetchPatientCases$(
          of(
            fetchPatientCases({
              patientId: String(patientId),
            }),
          ) as Observable<any>,
          state$,
          { getApi$, postApi$ },
        ).pipe(
          map(result => {
            const payload = result.payload as Record<string, unknown>;
            const [caseId] = Object.keys(payload);
            if (result.type === FETCH_CASES_SUCCESS && caseId) {
              // Update payload with correct case id
              action.payload.caseId = Number(caseId);
            }
            return action; // pass hopefully updated payload to next item in chain
          }),
        );
      }
      return of(action);
    }),
    switchMap(action => {
      const {
        dateTime,
        type,
        therapistId,
        patientId,
        duration,
        caseId,
        recurrence,
        recursUntil,
      } = action.payload;
      const userId = getId(state$.value);

      const patientCase = getCaseForPatient(state$.value, { patientId });
      if (!caseId && !patientCase) {
        toast.error('Could not find case ID matching patient');
        return of(
          bookAppointmentFailure({
            name: 'Error',
            message: 'Could not find case ID matching patient',
          }),
        );
      }

      // Offline guard: create temp appointments and enqueue for later sync
      if (getCurrentConnectionStatus() === 'offline') {
        return handleOfflineBooking(action.payload, state$);
      }

      return postApi$({
        endpoint: APPOINTMENTS_ENDPOINT,
        payload: {
          app_datetime: dateTime,
          app_type: type,
          case_id: caseId || patientCase.id,
          duration,
          patient_id: patientId,
          therapist_id: therapistId || userId,
          user_id: userId,
          recurrence,
          recurs_until: recursUntil,
        },
        // Minted per request here, because this path has no queue item to
        //   carry a key. It still covers the retry that matters at this layer:
        //   the response interceptor replays the same config after refreshing
        //   an expired token, and that replay reuses this header rather than
        //   asking for a second appointment.
        //
        // A therapist tapping Book again after an error does produce a new key,
        //   and deliberately so — the app cannot tell a repeat of one intent
        //   from a second intent.
        //
        // **That case is not covered, only improved.** Sending one instant per
        //   slot (`utils/appointment/toSlotDateTime`) is what gives the backend
        //   a duplicate it can refuse, and on 2026-08-11 an on-grid repeat was
        //   in fact refused. But five requests carrying a byte-identical
        //   off-grid time were all accepted, so "identical times collide" is an
        //   observation about Clinicient rather than documented behaviour, and
        //   the zeroing is probably what lets its check fire at all.
        //
        // So this is the one booking path with no second layer under it. It is
        //   strictly better than before, where nothing held it back. The
        //   backend now logs Clinicient's whole refusal body, so the first real
        //   refusal will replace the inference with the rule.
        headers: {
          ...getAuthHeaders(state$.value),
          ...getIdempotencyHeaders(randomUUID()),
        },
      }).pipe(
        handleAjaxSuccess(
          bookAppointmentSuccess,
          bookAppointmentCallTranslator,
        ),
        handleAjaxError(bookAppointmentFailure),
      );
    }),
  );
